import { notifyBrandBackdrop } from '../brand-tone.js';

export class TransitionRenderer {
  constructor(canvas, effects, options, onFailure = () => {}) {
    this.canvas = canvas;
    this.effects = effects;
    this.effect = Object.hasOwn(effects, options.effect) ? options.effect : 'flyeye';
    this.options = options;
    this.onFailure = onFailure;
    this.textures = [];
    this.shaders = [];
    this.programs = new Map();
    this.buffers = [];
    this.frame = 0;
    this.current = 0;
    this.destroyed = false;
    this.ready = false;
    this.suspended = false;
    this.onContextLost = (event) => { event.preventDefault(); this.fail(); };
    canvas.addEventListener('webglcontextlost', this.onContextLost);
  }

  async init(images) {
    try {
      this.gl = this.canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, powerPreference: 'low-power' });
      if (!this.gl) throw new Error('WebGL is unavailable');
      const gl = this.gl;
      for (const [name, definition] of Object.entries(this.effects)) {
        const program = gl.createProgram();
        const record = { program };
        this.programs.set(name, record);
        gl.attachShader(program, this.compile(gl.VERTEX_SHADER, definition.vertex));
        gl.attachShader(program, this.compile(gl.FRAGMENT_SHADER, definition.fragment));
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
        record.uniforms = Object.fromEntries(['from', 'to', 'progress', 'view_ratio', 'from_ratio', 'to_ratio', 'strength', 'frequency', 'dispersion'].map((key) => [key, gl.getUniformLocation(program, `u_${key}`)]));
        const mesh = definition.mesh ? polygonMesh() : { vertices: [-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1], stride: 2, attributes: [['position', 2, 0]] };
        record.buffer = gl.createBuffer();
        this.buffers.push(record.buffer);
        gl.bindBuffer(gl.ARRAY_BUFFER, record.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mesh.vertices), gl.STATIC_DRAW);
        record.count = mesh.vertices.length / mesh.stride;
        record.stride = mesh.stride * Float32Array.BYTES_PER_ELEMENT;
        record.attributes = mesh.attributes.map(([key, size, offset]) => ({ location: gl.getAttribLocation(program, `a_${key}`), size, offset: offset * Float32Array.BYTES_PER_ELEMENT }));
      }

      const decoded = await Promise.all(images.map(({ src }) => this.loadImage(src)));
      // 読込中にエリア表示が始まった場合、テクスチャ転送も完了後へ送る。
      while (this.suspended && !this.destroyed) await new Promise((resolve) => { this.resumeInitialization = resolve; });
      if (this.destroyed) return false;
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      for (const image of decoded) {
        const texture = gl.createTexture();
        this.textures.push({ texture, ratio: image.naturalWidth / image.naturalHeight });
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
      }
      this.ready = true;
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(this.canvas);
      this.resize();
      return true;
    } catch {
      this.fail();
      return false;
    }
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Unable to load image: ${src}`));
      image.src = src;
    });
  }

  compile(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    this.shaders.push(shader);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
    return shader;
  }

  resize() {
    if (!this.ready || this.suspended) return;
    const box = this.canvas.getBoundingClientRect();
    if (!box.width || !box.height) return;
    // 高DPR端末で描画面積を増やしすぎない。
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    this.canvas.width = Math.round(box.width * ratio);
    this.canvas.height = Math.round(box.height * ratio);
    this.draw(this.current, this.target ?? this.current, this.progress ?? 1);
  }

  draw(from, to, progress) {
    if (!this.ready || this.destroyed || this.suspended) return;
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.disable(gl.BLEND);
    const mesh = this.effects[this.effect].mesh;
    if (mesh && from !== to && progress > 0 && progress < 1) {
      // 次の画像を全面に描き、その上で現在の画像のピースを落とす。
      this.drawPass(this.programs.get('flyeye'), to, to, 1);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      this.drawPass(this.programs.get(this.effect), from, to, progress);
      gl.disable(gl.BLEND);
    } else {
      this.drawPass(this.programs.get(mesh ? 'flyeye' : this.effect), from, to, mesh ? (progress >= 1 ? 1 : 0) : progress);
    }
    notifyBrandBackdrop(this.canvas, progress >= 1);
  }

  drawPass(record, from, to, progress) {
    const gl = this.gl;
    const uniforms = record.uniforms;
    gl.useProgram(record.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, record.buffer);
    for (const attribute of record.attributes) {
      gl.enableVertexAttribArray(attribute.location);
      gl.vertexAttribPointer(attribute.location, attribute.size, gl.FLOAT, false, record.stride, attribute.offset);
    }
    for (const [unit, index, name] of [[0, from, 'from'], [1, to, 'to']]) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, this.textures[index].texture);
      gl.uniform1i(uniforms[name], unit);
      gl.uniform1f(uniforms[`${name}_ratio`], this.textures[index].ratio);
    }
    gl.uniform1f(uniforms.progress, progress);
    gl.uniform1f(uniforms.view_ratio, this.canvas.width / this.canvas.height);
    gl.uniform1f(uniforms.strength, this.options.strength);
    gl.uniform1f(uniforms.frequency, this.options.frequency);
    gl.uniform1f(uniforms.dispersion, this.options.dispersion);
    gl.drawArrays(gl.TRIANGLES, 0, record.count);
    for (const attribute of record.attributes) gl.disableVertexAttribArray(attribute.location);
  }

  setEffect(effect) {
    if (this.destroyed || !Object.hasOwn(this.effects, effect)) return false;
    if (effect === this.effect) return true;
    this.finish();
    this.effect = effect;
    this.draw(this.current, this.current, 1);
    return true;
  }

  goTo(index, duration = this.options.duration) {
    if (!this.ready || this.destroyed || !this.textures[index]) return;
    this.finish();
    this.target = index;
    if (this.suspended) { this.finish(); return; }
    if (!duration || index === this.current) { this.finish(); return; }
    const start = performance.now();
    const tick = (now) => {
      this.progress = Math.min(1, (now - start) / duration);
      this.draw(this.current, this.target, this.progress);
      if (this.progress < 1) this.frame = requestAnimationFrame(tick);
      else { this.current = this.target; this.target = null; this.frame = 0; }
    };
    this.frame = requestAnimationFrame(tick);
  }

  finish() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (this.target !== null && this.target !== undefined) this.current = this.target;
    this.target = null;
    this.progress = 1;
    this.draw(this.current, this.current, 1);
  }

  setSuspended(value) {
    if (this.destroyed || this.suspended === value) return;
    // 進行中の切り替えだけを移動先へ確定し、休止中の描画をなくす。
    if (value && this.frame) this.finish();
    this.suspended = value;
    if (!value) {
      this.resumeInitialization?.();
      this.resumeInitialization = null;
      this.resize();
    }
  }

  fail() {
    if (this.destroyed) return;
    this.destroy();
    this.onFailure();
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.resumeInitialization?.();
    this.resumeInitialization = null;
    this.ready = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    if (this.gl) {
      this.textures.forEach(({ texture }) => this.gl.deleteTexture(texture));
      this.shaders.forEach((shader) => this.gl.deleteShader(shader));
      this.buffers.forEach((buffer) => this.gl.deleteBuffer(buffer));
      this.programs.forEach(({ program }) => this.gl.deleteProgram(program));
    }
  }
}

// 隣接ピースで頂点を共有し、静止時に隙間が出ない不規則な三角形を作る。
export function polygonMesh(columns = 12, rows = 8) {
  const noise = (x, y) => { const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return value - Math.floor(value); };
  const point = (x, y) => [
    (x + (x > 0 && x < columns ? (noise(x, y) - .5) * .55 : 0)) / columns,
    (y + (y > 0 && y < rows ? (noise(y + 17, x) - .5) * .55 : 0)) / rows,
  ];
  const vertices = [];
  for (let y = 0; y < rows; y++) for (let x = 0; x < columns; x++) {
    const a = point(x, y), b = point(x + 1, y), c = point(x, y + 1), d = point(x + 1, y + 1);
    const triangles = (x + y) % 2 ? [[a, b, d], [a, d, c]] : [[a, b, c], [c, b, d]];
    triangles.forEach((triangle, index) => {
      const center = [0, 1].map((axis) => triangle.reduce((sum, vertex) => sum + vertex[axis], 0) / 3);
      const seed = noise(x * 2 + index + 1, y + 1);
      triangle.forEach(([px, py]) => vertices.push(px * 2 - 1, py * 2 - 1, ...center, seed));
    });
  }
  return { vertices, stride: 5, attributes: [['position', 2, 0], ['center', 2, 2], ['seed', 1, 4]] };
}
